/**
 * When the next mint is due: the arithmetic of the session-token refresh, with no timer in it.
 *
 * {@link SessionTokenEngine} is a machine: it holds a timer, a generation counter and a run of
 * consecutive bad tokens, and none of that is reproducible from a golden file. What IS
 * reproducible is the decision the machine makes at each step: given a token's remaining life,
 * how long until the next mint, and is a timer armed at all. A second language reimplements THAT
 * and drives it with whatever its platform calls a timer.
 *
 * So the decision is a rule and the machine is an engine, which is the split the whole tree is
 * laid out along. The engine imports this and owns the `setTimeout`; `transport.session.refresh`
 * calls this directly with the contract's own inputs. A conformance runner that restated the
 * arithmetic inline and string-matched the constants out of the engine's source would test how
 * a number is spelled rather than what it decides, and would stay green with the engine's
 * scheduling deleted.
 */

/** How long before expiry to refresh. A minute absorbs clock skew and a slow mint. */
export const REFRESH_MARGIN_MS = 60_000;

/**
 * Never ask setTimeout for a single wait longer than this.
 *
 * A day-long token would otherwise arm a day-long timer, and a backgrounded or suspended tab
 * throttles or drops one: it wakes holding a token that lapsed hours ago. Long waits are
 * chained through hops of this length instead, and each hop re-reads the wall clock rather than
 * trusting the timer that woke it, so a laptop resuming from sleep refreshes at the first hop
 * instead of on the schedule it slept through.
 *
 * A hop is not a mint. Re-minting at the cap would cost a long-lived tab a call to the
 * application's own endpoint every ten minutes for a token good for hours.
 *
 * It is a property of the driving, not of the decision, which is why {@link refreshSchedule}
 * never reads it: the delay it reports is the whole wait, and chopping that into hops is the
 * engine's job.
 */
export const MAX_TIMER_MS = 10 * 60_000;

/**
 * Never schedule a timer sooner than this.
 *
 * The margin is proportional, so a short-lived token still gets headroom rather than a zero
 * delay, but a token that arrives already expired, or a clock far enough out of step to look
 * that way, leaves no headroom to be proportional about.
 *
 * A flat floor turns that case into a call to the application's own mint endpoint every five
 * seconds for as long as the skew lasts, which is a slower loop rather than an end to one. So
 * the floor is only the FIRST step: each consecutive token that arrives with no usable lifetime
 * doubles the wait, up to REFRESH_MARGIN_MS, the same interval a failed mint waits. The first
 * healthy token resets it, so the cost of a skew that clears is one short wait rather than a
 * schedule the caller never climbs back out of.
 */
export const MIN_TIMER_MS = 5_000;

/**
 * What the caller knows when it asks for the next delay.
 *
 * The two fields are alternatives rather than a pair, because they answer different questions: a
 * lifetime asks what the token's own headroom implies, and a spent count asks how long to wait
 * when it implied nothing usable. A caller holding both asks twice, in that order, which is
 * exactly the sequence {@link SessionTokenEngine} runs.
 */
interface RefreshScheduleInput {
  /**
   * Milliseconds of life left in the token that just arrived.
   *
   * `undefined` is the token whose `expires_at` did not parse: the token itself is fine and
   * only its lifetime is unknown, so it is re-minted on the flat margin rather than treated as
   * spent. A value at or below zero is NOT that case: it is a token that really did arrive with
   * nothing left, and it falls through to `consecutiveSpent`.
   */
  readonly lifetimeMs?: number | undefined;
  /**
   * How many tokens in the current run arrived with no usable lifetime, counting from one.
   *
   * Asked only after a lifetime reported `armed: false`. The run resets on the first healthy
   * token, so the wait describes the CURRENT skew rather than every one the process has seen.
   */
  readonly consecutiveSpent?: number | undefined;
}

/** When to mint next, and whether a timer is armed for it at all. */
interface RefreshSchedule {
  /**
   * False where the delay did not clear {@link MIN_TIMER_MS}.
   *
   * The delay is still reported, because it is the proportional margin the token earned and a
   * caller may want to say so; what it must not do is arm a timer on it.
   */
  readonly armed: boolean;
  /** The whole wait in milliseconds, before the engine chops it into {@link MAX_TIMER_MS} hops. */
  readonly delayMs: number;
}

/**
 * refreshSchedule answers when the next mint is due.
 *
 * The margin is capped at half the token's life. A flat minute of headroom is right for a token
 * that lives hours and wrong for one that lives thirty seconds: it puts the refresh before the
 * mint that produced it, which schedules at zero and mints again immediately, forever. Half a
 * lifetime keeps the headroom proportional, so a short-lived token refreshes often rather than
 * continuously.
 */
export const refreshSchedule = ({
  consecutiveSpent,
  lifetimeMs,
}: RefreshScheduleInput): RefreshSchedule => {
  // A run of spent tokens is answered before a lifetime is, because the caller that has counted
  // one has already established the lifetime told it nothing.
  if (consecutiveSpent !== undefined) {
    return {
      armed: true,
      delayMs: Math.min(REFRESH_MARGIN_MS, MIN_TIMER_MS * 2 ** (consecutiveSpent - 1)),
    };
  }

  // An expiry that did not parse. `getTime()` on an Invalid Date is NaN, NaN survives Math.min
  // unchanged and loses every comparison it is put through, so letting it reach the arithmetic
  // below would read as a token with no lifetime left: one page load, and the caller spends the
  // life of the process re-minting a perfectly good token against its OWN endpoint.
  if (lifetimeMs === undefined) {
    return { armed: true, delayMs: REFRESH_MARGIN_MS };
  }

  const delayMs = lifetimeMs - Math.min(REFRESH_MARGIN_MS, lifetimeMs / 2);

  return { armed: delayMs >= MIN_TIMER_MS, delayMs };
};
